import {useState, useEffect} from 'react'
import {useI18n} from '@/i18n'
import {Button} from '@/components/ui/button'
import {Dialog, DialogContent, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {ErrorBanner} from '@/components/ui/error-banner'
import {Alert, AlertDescription} from '@/components/ui/alert'
import {AlertTriangle, Info, Play} from 'lucide-react'
import {ButtonSpinner} from '@/components/shared/ButtonSpinner'
import {SqlEditorPanel} from '@/components/editors/SqlEditorPanel'
import {SqlAssistPanel} from '@/pages/reporting/SqlAssistPanel'
import {ResultTable} from '@/pages/reporting/ResultTable'
import {useSqlRunner} from '@/hooks/use-sql-runner'
import type {SqlExecute} from '@/pages/reporting/types'

interface SqlEditorDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    sql: string
    onApply: (sql: string) => void
    validateResult?: (result: SqlExecute) => string | null
    maxRows?: number
    showAssist?: boolean
    assistContext?: string
    dryRun?: boolean
    applyLabel?: string
    parametersHint?: string
}

export function SqlEditorDialog({open, onOpenChange, sql, onApply, validateResult, maxRows, showAssist, assistContext, dryRun, applyLabel, parametersHint}: SqlEditorDialogProps) {
    const i18n = useI18n()
    const [draft, setDraft] = useState(sql)
    const {isRunning, result, error, run, reset} = useSqlRunner(maxRows ?? 100, dryRun)

    useEffect(() => {
        if (open) { setDraft(sql); reset() }
    }, [open, reset]) // eslint-disable-line react-hooks/exhaustive-deps -- reset on open only

    // Update draft when sql prop changes while dialog is open (e.g., external suggestion)
    useEffect(() => {
        if (open && sql) { setDraft(sql) }
    }, [sql]) // eslint-disable-line react-hooks/exhaustive-deps -- update draft from external changes

    const validationError = result && validateResult ? validateResult(result) : null

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-4xl" data-testid="sql-editor-dialog">
                <DialogHeader className="flex flex-row items-center justify-between space-y-0">
                    <DialogTitle>{i18n.attributeDefinitions.sqlEditor}</DialogTitle>
                    <div className="flex items-center gap-2 mr-6">
                        <Button
                            variant="secondary"
                            onClick={() => run(draft)}
                            disabled={isRunning}
                            data-testid="btn-run-query"
                        >
                            {isRunning
                                ? <><ButtonSpinner/>{i18n.reporting.trySql}</>
                                : <><Play className="h-4 w-4 mr-2" />{i18n.reporting.trySql}</>}
                        </Button>
                        <Button
                            onClick={() => { onApply(draft); onOpenChange(false) }}
                            disabled={!!validationError || !draft.trim()}
                            data-testid="sql-editor-apply"
                        >
                            {applyLabel ?? i18n.common.apply}
                        </Button>
                    </div>
                </DialogHeader>

                {parametersHint && (
                    <Alert data-testid="sql-parameters-hint">
                        <Info className="h-4 w-4" />
                        <AlertDescription>{parametersHint}</AlertDescription>
                    </Alert>
                )}

                {/* eslint-disable-next-line i18next/no-literal-string -- CSS dimensions */}
                <SqlEditorPanel sql={draft} onChange={setDraft} onRun={() => run(draft)} isRunning={isRunning} showRunButton={false} minHeight="180px" maxHeight="250px" />

                {showAssist && (
                    <SqlAssistPanel
                        onSuggestion={setDraft}
                        errorContext={assistContext ?? error?.message ?? null}
                        currentSql={draft}
                        autoSubmit={!!assistContext}
                        mode={assistContext ? 'migration' : undefined} // eslint-disable-line i18next/no-literal-string
                    />
                )}

                {error && <ErrorBanner error={error} />}

                {validationError && (
                    <Alert variant="destructive" data-testid="sql-validation-error">
                        <AlertTriangle className="h-4 w-4" />
                        <AlertDescription>{validationError}</AlertDescription>
                    </Alert>
                )}

                {result && !validationError && (
                    <ResultTable result={result} queryId="" isRunning={isRunning} />
                )}
            </DialogContent>
        </Dialog>
    )
}
